'use client';

import {
	Button, Card,
	Icon,
	LinkBox,
	LinkOverlay,
	Stack, Text,
	useSlotRecipe,
} from '@chakra-ui/react';
import type { RecipeVariantProps } from '@chakra-ui/react';
import type { Route } from 'next';
import { useTranslations } from 'next-intl';
import { MdOutlineAccountBalanceWallet, MdOutlineLocationOn } from 'react-icons/md';

import type { SavedJobListItemVM } from '@/modules/job/view-models';
import { NextLinkButton } from '@/modules/navigation/presentation';
import { NextImageWrapper } from '@/modules/shared/presentation';
import type { savedJobCardSlotRecipe } from '@/theme/slot-recipes';

type InterestedJobCardProps = RecipeVariantProps<typeof savedJobCardSlotRecipe> & {
	interestedJob: SavedJobListItemVM

	onClickDelete?: () => void
};

export const JobCardSaved = ({
	interestedJob,
	onClickDelete,
	status,
}: InterestedJobCardProps) => {
	const recipe = useSlotRecipe({ key: 'savedJobCard' });
	const styles = recipe({ status });

	const t = useTranslations('interested.card');
	const salary = interestedJob.salary
		? `${interestedJob.salary.min} - ${interestedJob.salary.max} THB`
		: 'Not specified';
	const publicationPeriod = {
		endDate:   interestedJob.expiredAt,
		startDate: interestedJob.postedAt,
	};

	return (
		<LinkBox>
			<Card.Root css={styles.root}>
				<Card.Body css={styles.body}>
					<NextImageWrapper
						image={{
							alt: interestedJob.title,
							src: interestedJob.coverImage ?? '/images/default-cover.png',
						}}
						w="160px"
					/>
					<Stack flex={1} gap={2}>
						<LinkOverlay href={interestedJob.detailUrl ?? '#'}>
							<Text css={styles.textTitle}>
								{interestedJob.companyName}
							</Text>
						</LinkOverlay>
						<Text css={styles.textBody} fontWeight="bold">
							{interestedJob.title}
						</Text>
						<Text css={styles.textBody}>
							<Icon
								as={MdOutlineLocationOn}
								h={6}
								mr={2}
								w={6}
							/>
							{interestedJob.location}
						</Text>
						<Text css={styles.textBody}>
							<Icon
								as={MdOutlineAccountBalanceWallet}
								h={6}
								mr={2}
								w={6}
							/>
							{salary}
						</Text>
					</Stack>
					<NextLinkButton href={interestedJob.entryUrl as Route ?? '#'}>
						{t('cta.entry')}
					</NextLinkButton>
				</Card.Body>
				<Card.Footer css={styles.footer}>
					<Text>
						{t('period', publicationPeriod)}
					</Text>
					<Button onClick={onClickDelete} textStyle="caption" variant="plain">
						{t('cta.delete')}
					</Button>
				</Card.Footer>
			</Card.Root>
		</LinkBox>
	);
};
